import { dynamicRender, v2ConditionsToV1Statement } from '../form_tools/dynamicRender';
import { parseAnswerData, transpose } from '../component/Table/Util/Helpers';
import { calculateSectionsToShow } from '../component/Table/Util/RSUtil';
import { validatePattern } from '../../utils/inputValidation';

class FormValidator {
  static getDefaultAddressFieldConfig() {
    return {
      line1: { show: true, required: true },
      line2: { show: true, required: false },
      locality: { show: true, required: true },
      region: { show: true, required: true },
      postalCode: { show: true, required: true },
      country: { show: true, required: true },
    };
  }

  static normalizeAddressFieldConfig(addressFieldConfig) {
    const defaults = FormValidator.getDefaultAddressFieldConfig();
    const normalized = {
      ...defaults,
      ...(addressFieldConfig || {}),
    };

    Object.keys(normalized).forEach(key => {
      const merged = {
        ...(defaults[key] || { show: true, required: false }),
        ...(normalized[key] || {}),
      };
      if (!merged.show) {
        merged.required = false;
      }
      normalized[key] = merged;
    });

    return normalized;
  }

  static getAddressFieldValues(address) {
    const safeAddress = address || {};
    const formattedLines =
      typeof safeAddress.formatted === 'string' && safeAddress.formatted.length > 0
        ? safeAddress.formatted.split('\n')
        : [];

    return {
      line1: (formattedLines[0] || safeAddress.street_address || '').toString().trim(),
      line2: (formattedLines[1] || '').toString().trim(),
      locality: (safeAddress.locality || '').toString().trim(),
      region: (safeAddress.region || '').toString().trim(),
      postalCode: (safeAddress.postal_code || '').toString().trim(),
      country: (safeAddress.country || '').toString().trim(),
    };
  }

  static hasAnyAddressValue(fieldValues) {
    return Object.values(fieldValues).some(value => value);
  }

  static isAddressAnswerValid(component, answerEntry, dynamicRequired) {
    const rawAnswer =
      answerEntry && Array.isArray(answerEntry.answer) && answerEntry.answer[0]
        ? answerEntry.answer[0]
        : null;
    const address =
      rawAnswer && typeof rawAnswer === 'object' ? rawAnswer : {};
    const fieldConfig = FormValidator.normalizeAddressFieldConfig(
      component.addressFields || component.addressFieldConfig
    );
    const fieldValues = FormValidator.getAddressFieldValues(address);
    const hasAnyValue = FormValidator.hasAnyAddressValue(fieldValues);

    if (!dynamicRequired && !hasAnyValue) {
      return true;
    }

    const requiredKeys = Object.keys(fieldConfig).filter(
      key => fieldConfig[key].show && fieldConfig[key].required
    );
    return requiredKeys.every(key => Boolean(fieldValues[key]));
  }

  static checkAllAnswerOnAllPages(
    formData,
    pagesMeta,
    setStateCallback,
    isValidationEnabled = true
  ) {
    // Skip validation if disabled
    if (!isValidationEnabled) {
      return true;
    }

    if (formData.request_data) {
      let pagesKeys = Object.keys(formData.request_data);

      let required_list = [];
      for (let a = 0; a < pagesKeys.length; a++) {
        let validationResult = FormValidator.doCheckAllAnswers(
          pagesKeys[a],
          formData,
          null,
          isValidationEnabled
        );
        if (validationResult && validationResult.length > 0) {
          //failed validation
          let aPage = formData.request_data[pagesKeys[a]];

          let pageTitle = pagesMeta[a].pageTitle;
          let aResult = { pageTitle: pageTitle };

          for (let b = 0; b < validationResult.length; b++) {
            let aQuestionKey = validationResult[b];

            const aPageComponents = aPage.components || aPage.fields || [];
            //try to find the Q that failed validation
            for (let c = 0; c < aPageComponents.length; c++) {
              let aComponent = aPageComponents[c];
              if (aComponent.key == aQuestionKey) {
                //found the Q
                aResult.questionTitle =
                  aComponent.label || aComponent.views?.question?.view?.data?.default;
                required_list.push(aResult);
              }
            }
          }
        }
      }

      if (required_list.length > 0) {
        setStateCallback({
          pagesErrorDialog: true,
          pagesError: required_list,
        });
        return false;
      }
    }

    return true;
  }

  static doCheckAllAnswers(
    pageIndex,
    formData,
    formContext = null,
    isValidationEnabled = true
  ) {
    // Skip validation if disabled
    if (!isValidationEnabled) {
      return [];
    }

    if (pageIndex === undefined) {
      pageIndex = formData.current_index;
    }

    let page_data = formData.request_data[pageIndex].components || formData.request_data[pageIndex].fields || [];
    const answer = formData.answer_data;

    let required_list = [];
    for (let i in page_data) {
      const isV2Field = page_data[i].type && !page_data[i].views;
      let dynamicRequired = (() => {
        if (page_data[i].required === true) return true;
        if (page_data[i].required === false) return false;
        if (page_data[i].required && page_data[i].required.conditions) {
          return dynamicRender(formContext, false, v2ConditionsToV1Statement(page_data[i].required));
        }
        if (isV2Field) return false;
        return dynamicRender(formContext, false, page_data[i].required);
      })();
      if (page_data[i].key != 'main_page') {
        // Skip repeatable sections - they're handled by doCheckTableAnswers
        const format =
          page_data[i].type ||
          (page_data[i].views &&
          page_data[i].views.answer &&
          page_data[i].views.answer.view &&
          page_data[i].views.answer.view.format);

        const isRepeatableSection = format === 'repeatable_section';

        if (isRepeatableSection) {
          continue; // Skip - handled by table validation
        }
        const answerEntry = answer[page_data[i].key];

        let hasAnswer = false;
        if (
          format === 'address' &&
          answerEntry &&
          Array.isArray(answerEntry.answer) &&
          answerEntry.answer.length > 0 &&
          answerEntry.answer[0] &&
          typeof answerEntry.answer[0] === 'object'
        ) {
          const fieldValues = FormValidator.getAddressFieldValues(answerEntry.answer[0]);
          hasAnswer = FormValidator.hasAnyAddressValue(fieldValues);
        } else {
          hasAnswer =
            answerEntry &&
            answerEntry.answer.length > 0 &&
            answerEntry.answer[0] != null &&
            answerEntry.answer[0].toString();
        }

        console.log(
          'FormValidator - Component:',
          page_data[i].key,
          'Required:',
          dynamicRequired,
          'Has answer:',
          hasAnswer,
          'Answer data:',
          answer[page_data[i].key]
        );

        // Check required fields
        if (dynamicRequired && !hasAnswer) {
          required_list.push(page_data[i].key);
          continue;
        }

        if (format === 'address') {
          const isAddressValid = FormValidator.isAddressAnswerValid(
            page_data[i],
            answerEntry,
            dynamicRequired
          );
          if (!isAddressValid) {
            required_list.push(page_data[i].key);
          }
          continue;
        }

        // Only check validation if there is an answer
        if (hasAnswer && page_data[i].validation) {
          const passesValidation = validatePattern(
            answer[page_data[i].key].answer[0],
            page_data[i].validation
          );
          if (!passesValidation) {
            required_list.push(page_data[i].key);
          }
        }
      }
    }

    return required_list;
  }

  static doCheckTableAnswers(pageIndex, formData, isValidationEnabled = true) {
    // Skip validation if disabled
    if (!isValidationEnabled) {
      return {};
    }

    if (pageIndex === undefined) {
      pageIndex = formData.current_index;
    }

    let page_data = formData.request_data[pageIndex].components || formData.request_data[pageIndex].fields || [];
    const answer = formData.answer_data;

    let required_list_table = {};

    for (let i in page_data) {
      if (page_data[i].key != 'main_page') {
        const tableFormat =
          page_data[i].type ||
          (page_data[i]['views'] &&
          page_data[i]['views']['answer'] &&
          page_data[i]['views']['answer']['view'] &&
          page_data[i]['views']['answer']['view']['format']);

        if (
          tableFormat === 'table' ||
          tableFormat === 'repeatable_section'
        ) {
          const isV2Table = page_data[i].type && !page_data[i].views;
          let cols = isV2Table
            ? (page_data[i].columns || page_data[i].fields || []).map(c => ({
                ...c,
                columnName: c.columnName || c.label || '',
                id: c.id || c.key || '',
              }))
            : page_data[i]['views']?.['answer']?.['view']?.['data']?.['default'];
          let rowLabels = isV2Table
            ? page_data[i].rowLabels
            : page_data[i]['views']?.['answer']?.['view']?.['data']?.['rowLabels'];

          let tableAns = null;

          const isRS = tableFormat === 'repeatable_section';

          // If the answer exists, set it and check the data
          if (answer[page_data[i].key] && answer[page_data[i].key]['answer']) {
            const toShow = calculateSectionsToShow(answer[page_data[i].key]);
            tableAns = parseAnswerData(
              answer[page_data[i].key],
              cols,
              isRS ? 'rowsAsLists' : (
                page_data[i]['exportType'] === 'columns_as_lists' ? 'columnsAsLists'
                : page_data[i]['exportType'] === 'rows_as_lists' ? 'rowsAsLists'
                : page_data[i]['exportType']
              ),
              rowLabels,
              toShow
            );
          }

          if (tableAns == null) continue;

          let listOfRequiredNotAns = FormValidator.checkAllTableRequired(
            cols,
            tableAns,
            page_data[i]['columns'] || page_data[i]['tableCells'],
            rowLabels
          );
          if (listOfRequiredNotAns.length > 0) {
            required_list_table[page_data[i].key] = listOfRequiredNotAns;
          }
        }
      }
    }

    return required_list_table;
  }

  static getRowName(rowLabels, row) {
    // Safety checks
    if (
      !rowLabels ||
      !Array.isArray(rowLabels) ||
      row < 0 ||
      row >= rowLabels.length
    ) {
      return `Row ${row + 1}`; // Default fallback
    }

    const rowLabel = rowLabels[row];
    if (!rowLabel) {
      return `Row ${row + 1}`; // Default fallback
    }

    if (rowLabel.rowLabel && rowLabel.rowLabel != '') {
      return rowLabel.rowLabel;
    } else if (rowLabel.rowKey) {
      return rowLabel.rowKey.charAt(0).toUpperCase() + rowLabel.rowKey.slice(1);
    } else {
      return `Row ${row + 1}`; // Default fallback
    }
  }

  static checkAllTableRequired(cols, tableAns, tableCells, rowLabels) {
    try {
      let requiredList = [];

      // Safety checks
      if (
        !cols ||
        !Array.isArray(cols) ||
        !tableAns ||
        !Array.isArray(tableAns)
      ) {
        console.warn('checkAllTableRequired: Invalid input data', {
          cols,
          tableAns,
          tableCells,
          rowLabels,
        });
        return requiredList;
      }

      console.log(
        'checkAllTableRequired: About to transpose tableAns:',
        tableAns
      );
      console.log(
        'checkAllTableRequired: tableAns type:',
        typeof tableAns,
        'isArray:',
        Array.isArray(tableAns)
      );
      if (Array.isArray(tableAns)) {
        console.log('checkAllTableRequired: tableAns.length:', tableAns.length);
        tableAns.forEach((row, index) => {
          console.log(
            `checkAllTableRequired: row ${index}:`,
            row,
            'type:',
            typeof row,
            'isArray:',
            Array.isArray(row)
          );
        });
      }
      let transposeTable;
      try {
        transposeTable = transpose(tableAns);
        console.log('checkAllTableRequired: Transpose result:', transposeTable);
      } catch (transposeError) {
        console.error(
          'checkAllTableRequired: Transpose failed, skipping validation:',
          transposeError
        );
        return requiredList; // Skip validation if transpose fails
      }

      // Check if transpose returned valid data
      if (
        !transposeTable ||
        !Array.isArray(transposeTable) ||
        transposeTable.length === 0
      ) {
        console.warn(
          'checkAllTableRequired: Invalid transpose result:',
          transposeTable
        );
        return requiredList;
      }

      // Loop over columns
      for (let i = 0; i < cols.length; i++) {
        let currCol = cols[i];

        // Safety check for column
        if (!currCol || !currCol.columnName) continue;

        // Safety check for column values in transposed table
        if (
          i >= transposeTable.length ||
          !transposeTable[i] ||
          !Array.isArray(transposeTable[i])
        ) {
          continue;
        }

        // If not required and table cells exist,
        // We need to check the tableCells
        if (!currCol.required) {
          if (tableCells == null) continue; // Col isn't required & table cells don't exist

          // Safety check for tableCells structure
          if (!tableCells[currCol.id] || !Array.isArray(tableCells[currCol.id]))
            continue;

          // Need to check each cell
          let colValues = transposeTable[i];
          for (let row = 0; row < colValues.length; row++) {
            // Safety checks for tableCells access
            if (!tableCells[currCol.id][row]) continue;
            if (tableCells[currCol.id][row].isDisabled) continue; // If disabled, don't check
            if (!tableCells[currCol.id][row].isRequired) continue;

            // Safety check for colValues[row]
            if (!colValues[row]) continue;

            // Check the value
            if (currCol.type === 'datetime') {
              if (
                colValues[row].value &&
                colValues[row].value.calculatedValue === ''
              ) {
                requiredList.push(
                  `(${currCol.columnName}, ${FormValidator.getRowName(rowLabels, row)})`
                );
              }
            } else {
              if (colValues[row].calculatedValue === '') {
                requiredList.push(
                  `(${currCol.columnName}, ${FormValidator.getRowName(rowLabels, row)})`
                );
              }
            }
          }
        } else {
          // Need to check all in column are not empty
          let colValues = transposeTable[i];

          if (currCol.type === 'datetime') {
            if (
              colValues.some((datetimeCol, row) => {
                // Safety checks
                if (!datetimeCol) return false;

                // If tablecells exist and cell is disabled, return false
                if (
                  tableCells != null &&
                  tableCells[currCol.id] &&
                  tableCells[currCol.id][row] &&
                  tableCells[currCol.id][row].isDisabled
                )
                  return false;
                return (
                  datetimeCol.value && datetimeCol.value.calculatedValue === ''
                );
              })
            ) {
              requiredList.push(currCol.columnName);
            }
          } else {
            if (
              colValues.some((val, row) => {
                // Safety checks
                if (!val) return false;

                // If tablecells exist and cell is disabled, return false
                if (
                  tableCells != null &&
                  tableCells[currCol.id] &&
                  tableCells[currCol.id][row] &&
                  tableCells[currCol.id][row].isDisabled
                )
                  return false;
                return val.calculatedValue === '';
              })
            ) {
              requiredList.push(currCol.columnName);
            }
          }
        }
      }

      return requiredList;
    } catch (error) {
      console.error('checkAllTableRequired: Error occurred:', error, {
        cols,
        tableAns,
        tableCells,
        rowLabels,
      });
      return [];
    }
  }

  static clearDeletedFields(matchParams, formData) {
    //will only take care of removing deleted fields for test
    //as the components will change only for a test session
    let test_id = matchParams.test_id;
    let isTest = test_id !== undefined;

    if (isTest) {
      const answer = formData.answer_data;
      let components_key = [];
      //collect all the component keys for the given form
      for (
        let indexToSave = 0;
        indexToSave < Object.keys(formData.request_data).length;
        indexToSave++
      ) {
        let page_data = formData.request_data[indexToSave].components || formData.request_data[indexToSave].fields || [];
        for (let componentIndex in page_data) {
          components_key.push(page_data[componentIndex].key);
        }
      }
      for (let ans in answer) {
        if (!components_key.includes(ans)) {
          delete answer[ans];
        }
      }
    }
  }

  static checkAllAnswered(
    pageIndex,
    formData,
    formContext = null,
    setStateCallback = null,
    isValidationEnabled = true
  ) {
    // Skip validation if disabled
    if (!isValidationEnabled) {
      return true;
    }

    let required_list = FormValidator.doCheckAllAnswers(
      pageIndex,
      formData,
      formContext,
      isValidationEnabled
    );
    let required_list_table = FormValidator.doCheckTableAnswers(
      pageIndex,
      formData,
      isValidationEnabled
    );

    if (
      required_list.length > 0 ||
      Object.keys(required_list_table).length > 0
    ) {
      if (setStateCallback) {
        setStateCallback({
          required_list: required_list,
          required_list_table: required_list_table,
          showRequiredFieldsSnackbar: true,
        });
      }
      return false;
    }

    return true;
  }
}

export default FormValidator;
